import * as React from 'react';
import { AppContext } from '../../../App';
import { DialogContentText, Tooltip, IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { useReRenderPackage } from '../../hooks/packages/useReRenderPackage';
import { PackageStatuses } from '../../../types/packager-types';
import { AxiosError } from 'axios';
import { extractApiError } from '../../../utils/extractApiError';
import { isReRenderable } from '../../../utils/packageEditability';

export interface IPackageReRenderProps {
  packageId: string;
  packageStatus: PackageStatuses;
  transmittedAt?: string | null;
  isStale?: boolean | null;
}

const PackageReRender: React.FC<IPackageReRenderProps> = ({
  packageId,
  packageStatus,
  transmittedAt,
  isStale,
}) => {
  const [open, setOpen] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const theme = useTheme();

  const mutationReRender = useReRenderPackage({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packager'] });
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setToast({
        variant: 'success',
        open: true,
        message: 'Re-render started. The package will move to DRAFT for review.',
      });
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to re-render package'),
      });
    },
  });

  const handleClickOpen = () => setOpen(true);
  const handleCloseConfirmation = () => setOpen(false);

  const handleReRender = () => {
    mutationReRender.mutate(packageId);
    setOpen(false);
  };

  // Not while a render is in flight, frozen or orphaned, nor once delivered: a
  // shipped package is recreated with create-from-package instead.
  if (!isReRenderable(packageStatus, transmittedAt)) {
    return null;
  }

  return (
    <>
      <Tooltip
        placement="top"
        title={isStale ? 'Re-render Package (files are out of date)' : 'Re-render Package'}
        disableFocusListener
        disableTouchListener
      >
        <IconButton
          data-cy={`btn-re-render-package-${packageId}`}
          onClick={!mutationReRender.isPending ? handleClickOpen : undefined}
          disabled={mutationReRender.isPending}
          sx={{
            color: isStale ? theme.palette.warning.main : theme.palette.custom.lightGreen,
            '&:hover': {
              backgroundColor:
                (isStale ? theme.palette.warning.main : theme.palette.custom.lightGreen) + '20',
            },
          }}
        >
          <RefreshIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        loading={mutationReRender.isPending}
        cypressId="dialog-package-re-render"
      >
        <DialogTitleGlynt
          title="Re-render package"
          closeModal={handleCloseConfirmation}
          loading={mutationReRender.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>
            This rebuilds the package files from the documents currently in it, using the latest
            data. Documents are not added or removed &mdash; change what is included by editing the
            entries first.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            The package moves to DRAFT, so it will not be delivered until you verify it again.
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleReRender}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-package-re-render-modal-cancel"
          submitCypressId="btn-package-re-render-modal-yes"
          clearCypressId=""
          submitButtonText="Re-render"
          submitLoading={mutationReRender.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageReRender;
